"use client";

import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import {
  ExternalLink,
  Clock,
  CheckCircle,
  AlertCircle,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Rocket,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAccount } from "wagmi";
import { getExplorerUrl } from "@/lib/bridge/api";
import {
  useActivityPollingAPI,
  type ProcessedTransaction,
} from "@/hooks/useActivityPollingAPI";
import { useStakingSubgraph } from "@/hooks/useStakingSubgraph";
import type { StakingTransaction } from "@/types/staking";
import { SyncIndicator } from "@/components/test/SyncIndicator";
import { STAKING_VAULTS } from "@/lib/bridge/contracts";
import { useReleaseDeposit } from "@/hooks/useBridge";
import { useToast } from "@/hooks/use-toast";

const STATUS_CONFIG = {
  "pending-hold": {
    label: "Pending Hold",
    icon: Clock,
    description: "7-day escrow in progress",
  },
  "ready-for-execution": {
    label: "Ready to Execute",
    icon: CheckCircle,
    description: "Timelock complete, ready for execution",
  },
  bridging: {
    label: "Bridging",
    icon: AlertCircle,
    description: "Transaction processing",
  },
  completed: {
    label: "Completed",
    icon: CheckCircle,
    description: "Bridge completed",
  },
  failed: {
    label: "Failed",
    icon: AlertCircle,
    description: "Bridge failed",
  },
  // Staking status configurations
  pending: {
    label: "Pending",
    icon: Clock,
    description: "Transaction pending",
  },
  confirmed: {
    label: "Confirmed",
    icon: CheckCircle,
    description: "Transaction confirmed",
  },
};

// Helper function to format direction text
const formatDirection = (direction: "camp-to-eth" | "eth-to-camp") => {
  return direction === "camp-to-eth" ? "Camp → Ethereum" : "Ethereum → Camp";
};

// Helper function to get token symbol from vault address or staking token
const getTokenSymbolFromVault = (
  vaultAddress?: string,
  stakingToken?: string,
): string => {
  if (!vaultAddress) return "TOKEN";

  // Find the token by matching vault address
  for (const [, config] of Object.entries(STAKING_VAULTS)) {
    if (config.vault.address.toLowerCase() === vaultAddress.toLowerCase()) {
      return config.symbol;
    }
  }

  if (stakingToken) {
    // Native token (CAMP) uses zero address
    if (stakingToken === "0x0000000000000000000000000000000000000000") {
      return "CAMP";
    }

    // Check against known token contracts
    for (const [, config] of Object.entries(STAKING_VAULTS)) {
      const tokenContract = config.tokenContract as { address: string } | null;
      if (
        tokenContract?.address &&
        tokenContract.address.toLowerCase() === stakingToken.toLowerCase()
      ) {
        return config.symbol;
      }
    }
  }

  return "TOKEN"; // Fallback if vault not found
};

interface ActivityPanelProps {
  isConnected?: boolean;
  mode?: "bridge" | "staking";
  showFilters?: boolean;
  itemsPerPage?: number;
  selectedTokenVaultId?: string; // Vault ID for staking mode filtering
  compact?: boolean; // For dropdown usage
}

export function ActivityPanel({
  isConnected = false,
  mode = "bridge",
  showFilters = true,
  itemsPerPage = 5,
  selectedTokenVaultId,
  compact = false,
}: ActivityPanelProps) {
  useAccount();
  const [currentPage, setCurrentPage] = useState(1);
  const { releaseDeposit, releaseState } = useReleaseDeposit();
  const { toast } = useToast();
  const [releasingDepositId, setReleasingDepositId] = useState<string | null>(
    null,
  );

  // Use the new API polling hook that automatically updates every 10 seconds
  const intervalMs = 10000; // 10 seconds interval
  const {
    transactions,
    isLoading,
    error,
    // lastUpdated, isPolling
  } = useActivityPollingAPI(intervalMs);

  // For staking mode, use staking subgraph hook
  const {
    stakingActivity,
    isLoading: stakingLoading,
    error: stakingError,
    lastUpdated: stakingLastUpdated,
    isPolling: stakingIsPolling,
  } = useStakingSubgraph(intervalMs, selectedTokenVaultId);

  // Get the appropriate data based on mode
  const allTransactions =
    mode === "staking" ? stakingActivity.transactions : transactions;
  const currentLoading = mode === "staking" ? stakingLoading : isLoading;
  const currentError = mode === "staking" ? stakingError : error;

  const [filterStatus, setFilterStatus] = useState<string>("all");

  // Reset to page 1 when filter changes (only when filters are shown)
  useEffect(() => {
    if (showFilters) {
      setCurrentPage(1);
    }
  }, [filterStatus, showFilters]);

  // Filter transactions based on showFilters prop
  const filteredTransactions = showFilters
    ? allTransactions.filter(
        (tx) => filterStatus === "all" || tx.status === filterStatus,
      )
    : allTransactions.filter(
        (tx) => tx.status === "confirmed" || tx.status === "completed",
      );

  // Pagination calculations
  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedTransactions = filteredTransactions.slice(
    startIndex,
    endIndex,
  );

  // Pagination handlers
  const goToPrevious = () => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  };

  const goToNext = () => {
    setCurrentPage((prev) => Math.min(totalPages, prev + 1));
  };

  const getTimeRemaining = (unlockTime: Date) => {
    const now = new Date();
    const diff = unlockTime.getTime() - now.getTime();

    if (diff <= 0) return "Ready";

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    return `${days}d ${hours}h ${minutes}m`;
  };

  const getProgressPercentage = (unlockTime: Date, startTime: Date) => {
    const now = new Date();
    const total = unlockTime.getTime() - startTime.getTime();
    const elapsed = now.getTime() - startTime.getTime();
    return Math.min(100, Math.max(0, (elapsed / total) * 100));
  };

  const formatAmount = (amount: string) => {
    return new Intl.NumberFormat().format(parseFloat(amount));
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const CardComponent = compact ? "div" : Card;
  const CardHeaderComponent = compact ? "div" : CardHeader;
  const CardContentComponent = compact ? "div" : CardContent;

  return (
    <CardComponent
      className={cn(
        "flex h-full flex-col",
        compact
          ? "space-y-4"
          : "rounded-sm border border-[#261000]/15 bg-[#FDFDFC]",
      )}>
      {!compact && mode === "staking" && (
        <CardHeaderComponent className="flex-shrink-0">
          <div className="flex items-center justify-between">
            <CardTitle className="font-sans text-xl font-bold text-gray-900">
              Your Staking Activity
            </CardTitle>
            {isConnected && (
              <SyncIndicator
                lastUpdated={stakingLastUpdated}
                isLoading={stakingLoading}
                isPolling={stakingIsPolling}
                intervalMs={intervalMs}
              />
            )}
          </div>
        </CardHeaderComponent>
      )}

      <CardContentComponent
        className={cn(
          "flex flex-1 flex-col",
          compact ? "space-y-3" : "space-y-4",
        )}>
        {/* Filter Chips - only show when connected and showFilters is true */}
        {isConnected && showFilters && (
          <div className="flex flex-shrink-0 flex-wrap gap-2">
            {(mode === "staking"
              ? ["all", "pending", "confirmed", "failed"]
              : [
                  "all",
                  "pending-hold",
                  "ready-for-execution",
                  "bridging",
                  "completed",
                  "failed",
                ]
            ).map((status) => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={cn(
                  "rounded-full px-3 py-1 font-sans text-xs font-medium capitalize transition-all",
                  filterStatus === status
                    ? "bg-orange-500 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200",
                )}>
                {status.replace(/-/g, " ")}
              </button>
            ))}
          </div>
        )}

        {!isConnected ? (
          /* Wallet not connected message */
          <div
            className={cn(
              "flex items-center justify-center font-sans text-gray-500",
              compact ? "py-8" : "py-16",
            )}>
            <div className="font-sans-serif mt-1 text-center text-xs text-[#868686]">
              Connect wallet to view activity
            </div>
          </div>
        ) : currentLoading ? (
          /* Loading state */
          <div
            className={cn(
              "flex items-center justify-center font-sans text-gray-500",
              compact ? "py-8" : "py-16",
            )}>
            <div className="space-y-2 text-center">
              <Loader2 className="mx-auto h-6 w-6 animate-spin" />
              <div>Loading transaction history...</div>
            </div>
          </div>
        ) : currentError ? (
          /* Error state */
          <div
            className={cn(
              "flex items-center justify-center font-sans text-red-500",
              compact ? "py-8" : "py-16",
            )}>
            <div className="text-center">
              <div className="mb-2 text-red-600">⚠️ Error</div>
              <div className="text-sm">{currentError}</div>
            </div>
          </div>
        ) : (
          <>
            {/* Transaction List */}
            <div className="flex-1">
              <div className="space-y-3">
                {filteredTransactions.length === 0 ? (
                  <div
                    className={cn(
                      "font-sans-serif mt-1 text-center text-xs text-[#868686]",
                      compact ? "py-4" : "py-8",
                    )}>
                    {allTransactions.length === 0
                      ? mode === "staking"
                        ? showFilters
                          ? "No staking activity yet. Start staking to see your activity here."
                          : "No completed staking activity yet. Start staking to see your activity here."
                        : showFilters
                          ? "No bridge transactions yet. Start bridging to see your activity here."
                          : "No completed bridge transactions yet. Start bridging to see your activity here."
                      : showFilters
                        ? "No transactions found for the selected filter."
                        : "No completed transactions found."}
                  </div>
                ) : (
                  <>
                    {paginatedTransactions.map((tx) => {
                      const statusConfig = STATUS_CONFIG[tx.status];
                      const StatusIcon = statusConfig.icon;

                      // Handle different transaction types
                      const isStakingTx = mode === "staking";
                      const processedTx = tx as ProcessedTransaction;
                      const hasUnlockTime =
                        !isStakingTx && !!processedTx.unlockTimestamp;
                      const isUnlockTimeReached =
                        hasUnlockTime &&
                        processedTx.unlockTimestamp!.getTime() <= Date.now();
                      const tokenSymbol = isStakingTx
                        ? getTokenSymbolFromVault(
                            (tx as StakingTransaction).vault?.vaultAddress,
                            (tx as StakingTransaction).vault?.stakingToken,
                          )
                        : (tx as ProcessedTransaction).tokenSymbol || "CAMP";
                      const txTitle = isStakingTx
                        ? `${
                            (tx as StakingTransaction).type
                              .charAt(0)
                              .toUpperCase() +
                            (tx as StakingTransaction).type.slice(1)
                          }`
                        : formatDirection(
                            (tx as ProcessedTransaction).direction,
                          );

                      // Determine if release is allowed
                      const canRelease =
                        !isStakingTx &&
                        processedTx.tokenType === "CAMP" &&
                        processedTx.direction === "camp-to-eth" &&
                        !!processedTx.depositId &&
                        (processedTx.status === "ready-for-execution" ||
                          (processedTx.status === "pending-hold" &&
                            isUnlockTimeReached));

                      return (
                        <div
                          key={tx.id}
                          className={cn(
                            "rounded-sm border border-gray-200 bg-white transition-colors hover:border-gray-300",
                            compact ? "p-3" : "p-4",
                          )}>
                          <div className="flex items-start justify-between">
                            <div className="flex flex-1 items-start gap-3">
                              <div
                                className={cn(
                                  "mt-2 h-2 w-2 rounded-full",
                                  tx.status === "pending-hold" &&
                                    "bg-orange-400",
                                  tx.status === "ready-for-execution" &&
                                    "bg-green-400",
                                  tx.status === "bridging" && "bg-blue-400",
                                  tx.status === "completed" && "bg-green-600",
                                  tx.status === "failed" && "bg-red-600",
                                  tx.status === "pending" && "bg-orange-400",
                                  tx.status === "confirmed" && "bg-green-600",
                                )}
                              />

                              <div className="flex-1 space-y-2">
                                <div className="flex items-start justify-between">
                                  <div className="space-y-1">
                                    <div
                                      className={cn(
                                        "font-semibold text-gray-900",
                                        compact ? "text-sm" : "text-lg",
                                      )}>
                                      {formatAmount(tx.amount)} {tokenSymbol}
                                    </div>
                                    <div className="flex items-center gap-2 text-sm text-gray-600">
                                      <span className="font-medium">
                                        {txTitle}
                                      </span>
                                      {isStakingTx &&
                                        (tx as StakingTransaction).vault && (
                                          <Badge
                                            variant="outline"
                                            className="border-blue-200 bg-blue-50 text-xs text-blue-700">
                                            {tokenSymbol}
                                          </Badge>
                                        )}
                                      {!isStakingTx &&
                                        (tx as ProcessedTransaction)
                                          .tokenType && (
                                          <Badge
                                            variant="outline"
                                            className={cn(
                                              "text-xs",
                                              (tx as ProcessedTransaction)
                                                .tokenType === "CAMP" &&
                                                "border-orange-200 bg-orange-50 text-orange-700",
                                              (tx as ProcessedTransaction)
                                                .tokenType === "ETH" &&
                                                "border-blue-200 bg-blue-50 text-blue-700",
                                              (tx as ProcessedTransaction)
                                                .tokenType === "USDC" &&
                                                "border-green-200 bg-green-50 text-green-700",
                                            )}>
                                            {
                                              (tx as ProcessedTransaction)
                                                .tokenType
                                            }
                                          </Badge>
                                        )}
                                    </div>
                                    <div className="text-sm text-gray-500">
                                      {formatDate(tx.timestamp)}
                                    </div>
                                  </div>

                                  <Badge
                                    variant="outline"
                                    className={cn(
                                      "border-gray-200",
                                      tx.status === "pending-hold" &&
                                        "border-orange-200 bg-orange-50 text-orange-600",
                                      tx.status === "ready-for-execution" &&
                                        "border-green-200 bg-green-50 text-green-600",
                                      tx.status === "bridging" &&
                                        "border-blue-200 bg-blue-50 text-blue-600",
                                      tx.status === "completed" &&
                                        "border-green-200 bg-green-50 text-green-700",
                                      tx.status === "failed" &&
                                        "border-red-200 bg-red-50 text-red-600",
                                      // Staking status colors
                                      tx.status === "pending" &&
                                        "border-orange-200 bg-orange-50 text-orange-600",
                                      tx.status === "confirmed" &&
                                        "border-green-200 bg-green-50 text-green-600",
                                    )}>
                                    <StatusIcon className="mr-1 h-3 w-3" />
                                    {statusConfig.label}
                                  </Badge>
                                </div>

                                {!isStakingTx &&
                                  (tx.status === "pending-hold" ||
                                    tx.status === "ready-for-execution") &&
                                  (tx as ProcessedTransaction)
                                    .unlockTimestamp && (
                                    <div className="space-y-2">
                                      <div className="flex justify-between text-sm">
                                        <span className="text-gray-500">
                                          {tx.status === "ready-for-execution"
                                            ? "Status"
                                            : "Time Remaining"}
                                        </span>
                                        <span
                                          className={cn(
                                            "font-medium",
                                            tx.status === "ready-for-execution"
                                              ? "text-green-600"
                                              : "text-orange-600",
                                          )}>
                                          {tx.status === "ready-for-execution"
                                            ? "Ready to Execute"
                                            : getTimeRemaining(
                                                (tx as ProcessedTransaction)
                                                  .unlockTimestamp!,
                                              )}
                                        </span>
                                      </div>
                                      <Progress
                                        value={
                                          tx.status === "ready-for-execution"
                                            ? 100
                                            : getProgressPercentage(
                                                (tx as ProcessedTransaction)
                                                  .unlockTimestamp!,
                                                tx.timestamp,
                                              )
                                        }
                                        className={cn(
                                          "h-2 bg-gray-200",
                                          tx.status === "ready-for-execution"
                                            ? "[&>div]:bg-green-500"
                                            : "[&>div]:bg-orange-500",
                                        )}
                                      />
                                    </div>
                                  )}

                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    {tx.txHash && (
                                      <a
                                        href={
                                          isStakingTx
                                            ? `https://camp.cloud.blockscout.com/tx/${tx.txHash}` // For staking, use Camp Network explorer
                                            : getExplorerUrl(
                                                tx.txHash,
                                                (tx as ProcessedTransaction)
                                                  .direction,
                                                tx.status,
                                              )
                                        }
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-1 text-sm text-blue-600 transition-colors hover:text-blue-700">
                                        {tx.txHash.slice(0, 6)}...
                                        {tx.txHash.slice(-4)}
                                        <ExternalLink className="h-3 w-3" />
                                      </a>
                                    )}
                                  </div>
                                  {/* Release button for ready-to-execute CAMP bridge transactions */}
                                  {canRelease && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      disabled={
                                        releaseState.isLoading ||
                                        releasingDepositId === tx.id
                                      }
                                      onClick={async () => {
                                        const depositId = (
                                          tx as ProcessedTransaction
                                        ).depositId!;
                                        setReleasingDepositId(tx.id);
                                        try {
                                          await releaseDeposit(depositId);
                                          toast({
                                            title: "🚀 Release initiated!",
                                            description:
                                              "Your CAMP tokens are being released to Ethereum.",
                                          });
                                        } catch (error) {
                                          console.error(
                                            "Failed to release deposit:",
                                            error,
                                          );
                                          toast({
                                            title: "❌ Release failed",
                                            description:
                                              error instanceof Error
                                                ? error.message
                                                : "Failed to release deposit",
                                            variant: "destructive",
                                          });
                                        } finally {
                                          setReleasingDepositId(null);
                                        }
                                      }}
                                      className="flex cursor-pointer items-center gap-1 px-2 py-1 text-xs font-medium disabled:cursor-not-allowed">
                                      {releasingDepositId === tx.id ? (
                                        <>
                                          <Loader2 className="h-3 w-3 animate-spin" />
                                          Releasing...
                                        </>
                                      ) : (
                                        <>
                                          <Rocket className="h-3 w-3" />
                                          Release
                                        </>
                                      )}
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                      <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-4">
                        <div className="text-sm text-gray-500">
                          Showing {startIndex + 1}-
                          {Math.min(endIndex, filteredTransactions.length)} of{" "}
                          {filteredTransactions.length} transactions
                        </div>

                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={goToPrevious}
                            disabled={currentPage === 1}
                            className="px-2 hover:border-orange-200 hover:bg-orange-50">
                            <ChevronLeft className="h-4 w-4" />
                          </Button>

                          <span className="px-2 text-sm text-gray-600">
                            Page {currentPage} of {totalPages}
                          </span>

                          <Button
                            variant="outline"
                            size="sm"
                            onClick={goToNext}
                            disabled={currentPage === totalPages}
                            className="px-2 hover:border-orange-200 hover:bg-orange-50">
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </CardContentComponent>
    </CardComponent>
  );
}
